import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { ActionService, cleanObject, CustomFormatter, CustomValidators, ISitemap, ISitemapCommand, ISitemapModel, SITEMAP } from 'pbc.angular';

import { IFeldUnterkategorie, IPostFeldUnterkategorieResponse } from 'fa-kt.types';

import { toInitials } from 'pbc.functions';
import { FeldUnterkategoriesService } from '../../../query';
import { PostFeldUnterkategorieCommandService } from '../service';

interface IFormValue {
  feldUnterkategorie?: IFeldUnterkategorie;
}

@Component({
  selector: 'fa-kt-post-feld-unterkategorie',
  templateUrl: './post-feld-unterkategorie.component.html',
  styleUrls: ['./post-feld-unterkategorie.component.css'],
})
export class PostFeldUnterkategorieCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;
  model!: ISitemapModel;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IFormValue) {
    this.patch(value);
  }
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostFeldUnterkategorieResponse> = new EventEmitter<IPostFeldUnterkategorieResponse>();

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private actions: ActionService,
    private message: NzMessageService,
    public feldUnterkategories: FeldUnterkategoriesService,
    public postFeldUnterkategorie: PostFeldUnterkategorieCommandService /* << dependencies    */ /*    dependencies >> */,
  ) {
    this.command = sitemap.FELDER.Commands.POST_FELD_UNTERKATEGORIE;
    this.model = sitemap.FELDER.Models.FELD_UNTERKATEGORIE;
    /* << constructor    */
    /*    constructor >> */
    this.form = this.fb.group({
      feldUnterkategorie: this.fb.group({
        id: [null, []],
        feldKategorie: [null, [Validators.required]],
        name: [null, [Validators.required]],
        initialen: [null, [Validators.required]],
        kommentar: [null, []],
        color: [null, []],
        icon: [null, []],
        order: [null, []],
      }),
    });
    this.initialen?.setValidators([Validators.required, CustomValidators.isUnique(this.feldUnterkategories.initialenIsUnique(this.id))]);
    this.patch();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.form.valueChanges.subscribe((value: any) => {
        this.valid.emit(this.form.valid);
        if (this.form.valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.id?.valueChanges.subscribe(async (id) => {
        /* << on-id-change    */
        /*    on-id-change >> */
      }) as Subscription,
      this.feldKategorie?.valueChanges.subscribe(async (feldKategorie) => {
        /* << on-feld-kategorie-change    */
        /*    on-feld-kategorie-change >> */
      }) as Subscription,
      this.name?.valueChanges.subscribe(async (name) => {
        /* << on-name-change    */
        /*    on-name-change >> */
      }) as Subscription,
      this.initialen?.valueChanges.subscribe(async (initialen) => {
        /* << on-initialen-change    */
        /*    on-initialen-change >> */
      }) as Subscription,
      this.kommentar?.valueChanges.subscribe(async (kommentar) => {
        /* << on-kommentar-change    */
        /*    on-kommentar-change >> */
      }) as Subscription,
      /* << subscibe    */
      this.name?.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((name: string) => {
        if (this.initialen && (!this.initialen.touched || !this.initialen.value)) {
          this.initialen.patchValue(toInitials(name));
        }
      }) as Subscription,
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IFormValue) {
    let feldUnterkategorie = {
      id: null,
      feldKategorie: null,
      name: null,
      initialen: null,
      kommentar: null,
      order: 0,
      color: '',
      icon: '',
    };
    if (value && value.feldUnterkategorie) {
      feldUnterkategorie = {
        ...feldUnterkategorie,
        ...cleanObject(value.feldUnterkategorie),
      };
    }
    this.form.patchValue({ feldUnterkategorie });
    /* << custom-stuff    */
    /*    custom-stuff >> */
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload && payload.feldUnterkategorie) {
        payload.feldUnterkategorie = cleanObject(payload.feldUnterkategorie);
      }
      const response: IPostFeldUnterkategorieResponse | undefined = await this.postFeldUnterkategorie.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls.objektArt.get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get id() {
    return this.form.controls.feldUnterkategorie.get('id');
  }

  get feldKategorie() {
    return this.form.controls.feldUnterkategorie.get('feldKategorie');
  }

  get name() {
    return this.form.controls.feldUnterkategorie.get('name');
  }

  get initialen() {
    return this.form.controls.feldUnterkategorie.get('initialen');
  }

  get kommentar() {
    return this.form.controls.feldUnterkategorie.get('kommentar');
  }
}
